import { AddCard, BarChart, ExitToApp, ManageAccounts, Payments, Search, SyncLock } from "@mui/icons-material";
import { AppBar, Button, Card, CardContent, CircularProgress, Divider, Drawer, FormControl, Grid2, IconButton, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Select, styled, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Toolbar, Typography } from "@mui/material";
import { motion } from "framer-motion";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo-only-light.png";
import { BASE_URL } from "../url";
import theme from "./theme";

const CASHIN_FEE  = 1.3
const CASHOUT_FEE = 6.0

const StyledCard = styled(Card)({
    // borderRadius: "0",
    width: "100%",
});

const StyledButton = styled(Button)({
    margin: "8px",
    textTransform: "none",
    // borderRadius: "0",
    boxShadow: "none",
    color: "white",
});

const StyledDrawer = styled(Drawer)({
    width: "214px",
    height: "100%",
    [`& .MuiDrawer-paper`]: {position: 'relative'},
})

const FlatTextField = styled(TextField)({
    ".MuiInputBase-root": {
    }
});

const FlatSelectField = styled(Select)({
    ".MuiInputBase-root": {
        // borderRadius: "0",
    }
});

const GradientAppBar = styled(AppBar)({
    zIndex: theme.zIndex.drawer + 1,
    color: 'whitesmoke'
})

const DashboardBody = styled(Grid2)({
    backgroundColor: "#f8f9fa",
    overflow       : "auto",
    minHeight      : "calc(100vh - 64px)",
    maxHeight      : "calc(100vh - 64px)",
    marginTop      : "64px"
})

const StyledListItemButton = styled(ListItemButton)({
    [`&.Mui-selected .MuiListItemText-root`]: {
        color: theme.palette.primary.main
    }
})

const StyledTableCell = styled(TableCell)({
    whiteSpace: 'nowrap'
})

const statusColors: any = {
    SUCCESS: 'success',
    FAILED : 'error',
    PENDING: 'warning',
    EXPIRED: 'textSecondary'
}

const itemsPerPage = 3;

const Dashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<any[]>([]);
    const [paymentSummary, setPaymentSummary] = useState({
        TOTAL      : 0,
        COUNT      : 0,
        SUCCESS    : 0,
        PENDING    : 0,
        CLOSED     : 0,
        FAILED     : 0,
        TRX_PER_MIN: 0,
    });
    const [disburseSummary, setDisburseSummary] = useState({
        TOTAL      : 0,
        COUNT      : 0,
        SUCCESS    : 0,
        PENDING    : 0,
        CLOSED     : 0,
        FAILED     : 0,
        TRX_PER_MIN: 0,
        BALANCE    : 0,
    });

    const [transaction_type, setTransactionType] = useState('');

    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [cutoff, setCutoff] = useState<'All' | '12AM' | '8AM' | '4PM'>('All');

    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("All");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(1000);
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [orderBy, setOrderBy] = useState("created_at")

    const [downloading, setDownloading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [reset, setReset] = useState(false);
    const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement>();
    const mounted = useRef(false);

    let cutoffs = {
        "12AM": {start_time: '00:00:00', end_time: '07:59:59.999999'},
        "8AM" : {start_time: '08:00:00', end_time: '15:59:59.999999'},
        "4PM" : {start_time: '16:00:00', end_time: '23:59:59.999999'},
    }
    const descendingComparator = (a: any, b: any, orderBy: string) => {
        if (b[orderBy] < a[orderBy]) {
          return -1;
        }
        if (b[orderBy] > a[orderBy]) {
          return 1;
        }
        return 0;
      }
      
    const getComparator = (order: "asc" | "desc", orderBy: string) => {
        return order === 'desc'
            ? (a: any, b: any) => descendingComparator(a, b, orderBy)
            : (a: any, b: any) => -descendingComparator(a, b, orderBy);
    }

    const sortedData = useMemo(
        () => [...data].sort(getComparator(order, orderBy)),
        [data, order, orderBy]
    )

    const visibleData = useMemo(() => {
        let visible = [...data]
            .filter(obj => JSON.stringify(obj).includes(search))
            .sort(getComparator(order, orderBy))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        setLoading(false);
        return visible;
    }, [sortedData, page, rowsPerPage, search]);

    const orderData = (key: string) => {
        setOrderBy(key)
        setOrder(key == orderBy? (order == 'asc'? 'desc': 'asc'): 'asc');
    }

    const download = () => {
        const token = JSON.parse(localStorage.getItem("token") || '{}');
        if (token && transaction_type) {
            setDownloading(true);
            let time = cutoff == 'All'? '': `start_time=${cutoffs[cutoff].start_time}&end_time=${cutoffs[cutoff].end_time}`;
            fetch(`${window.location.protocol}//${BASE_URL}/dashboard/transactions/${transaction_type}/download?status=${status == 'All'? '': status}&start=${date}&end=${date}&${time}`, {
                headers: {
                    Authorization: `Bearer ${token.access_token}`
                }
            })
            .then(res => res.status != 200? navigate('/login'): res)
            .then((res: any) => res.text())
            .then(url => {
                if (url) {
                    console.log(url)
                    let link = document.createElement('a');
                    link.href = url.replaceAll('"', '');
                    link.download = `${{
                        payment: 'Cash-In',
                        'fund-transfer': 'Cash-Out',
                    }[transaction_type]} Transactions ${moment(date).format('MMDDYY')} ${{
                        'All' : '12am-12am',
                        '12AM': '12am-8am',
                        '8AM' : '8am-4pm',
                        '4PM' : '4pm-12am'
                    }[cutoff]}.xlsx`;
                    link.target = "_blank";
                    link.click();
                    link.remove();
                }
            })
            .finally(() => setDownloading(false));
        }
    }

    const fetchSummary = async (token: any, type: string) => {
        setSummaryLoading(true);
        let time = cutoff == 'All'? '': `start_time=${cutoffs[cutoff].start_time}&end_time=${cutoffs[cutoff].end_time}`;
        return await fetch(
            `${window.location.protocol}//${BASE_URL}/dashboard/success-rate/${type}?start=${date}&end=${date}&${time}`,
            {
                headers: {
                    Authorization: `Bearer ${token.access_token}`
                }
            }
        )
        .then(res => res.json())
        .finally(() => setSummaryLoading(false))
    }

    const fetchTransactions = async (token: any) => {
        let time = cutoff == 'All'? '': `start_time=${cutoffs[cutoff].start_time}&end_time=${cutoffs[cutoff].end_time}`;
        fetch(`${window.location.protocol}//${BASE_URL}/dashboard/transactions/${transaction_type}?status=${status == 'All'? '': status}&start=${date}&end=${date}&${time}`, {
            headers: {
                Authorization: `Bearer ${token.access_token}`
            }
        })
        .then(res => res.status == 200? res.json(): navigate('/login'))
        .then(res => setData(res))
    }

    const loadData = async () => {
        setLoading(true);
        const token = JSON.parse(localStorage.getItem("token") || '{}');
        if (token) {
            transaction_type && fetchTransactions(token)
            await fetchSummary(token, 'payment').then(summary => setPaymentSummary(summary))
            await fetchSummary(token, 'fund-transfer').then(summary => setDisburseSummary(summary))
        }
    }

    const logout = () => {
        localStorage.removeItem('token')
        navigate('/login')
    }

    useEffect(() => {
        if (mounted.current) {
            loadData()
        } else {
            mounted.current = true;
        }
    }, [reset])

    useEffect(() => {
        setPaymentSummary({
            TOTAL      : 0,
            COUNT      : 0,
            SUCCESS    : 0,
            PENDING    : 0,
            CLOSED     : 0,
            FAILED     : 0,
            TRX_PER_MIN: 0
        })
        setDisburseSummary({
            TOTAL      : 0,
            COUNT      : 0,
            SUCCESS    : 0,
            PENDING    : 0,
            CLOSED     : 0,
            FAILED     : 0,
            TRX_PER_MIN: 0,
            BALANCE    : 0
        })
        setStatus('All')
        setDate(moment().format('YYYY-MM-DD'))
        setPage(0)
        setOrder('desc')
        setOrderBy('created_at')
        setReset(!reset)
    }, [transaction_type])

    return <>
        <GradientAppBar position="fixed">
            <motion.div
                initial    = {{ opacity: 0, x: -20 }}
                animate    = {{ opacity: 1, x: 0 }}
                transition = {{ duration: 1 }}
            >
                <Toolbar>
                    <Typography noWrap component="div" sx={{margin: '0 8px'}}>
                        <img src={logo} width="25px"/>
                    </Typography>
                    <Typography noWrap component="div" fontSize="18px" sx={{margin: '0 8px', flexGrow: 1}}>
                        Dashboard Portal
                    </Typography>
                    <IconButton color="secondary" onClick={(e) => setAccountMenuAnchor(e.currentTarget)}>
                        <ManageAccounts />
                    </IconButton>
                    <Menu
                        anchorEl = {accountMenuAnchor}
                        open     = {!!accountMenuAnchor}
                        onClose  = {() => setAccountMenuAnchor(undefined)}
                    >
                        <MenuItem onClick={() => navigate('/change-password')}>
                            <ListItemIcon>
                                <SyncLock fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Change Password</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={logout}>
                            <ListItemIcon>
                                <ExitToApp fontSize="small" color="error"/>
                            </ListItemIcon>
                            <ListItemText>Log out</ListItemText>
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </motion.div>
        </GradientAppBar>
        <DashboardBody container wrap="nowrap">
            <Grid2 sx={{minWidth: "214px"}}>
                <StyledDrawer variant="permanent" anchor="left">
                    <List sx={{height: '100%', overflowY: 'hidden'}}>
                        <motion.div
                            initial    = {{ opacity: 0, x: -20 }}
                            animate    = {{ opacity: 1, x: 0 }}
                            transition = {{ duration: 1 }}
                        >
                            <ListItem disablePadding>
                                <StyledListItemButton selected={!transaction_type} onClick={() => setTransactionType('')}>
                                    <ListItemIcon>
                                        <BarChart color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Dashboard" />
                                </StyledListItemButton>
                            </ListItem>
                        </motion.div>
                        <motion.div
                            initial    = {{ opacity: 0, x: -20 }}
                            animate    = {{ opacity: 1, x: 0 }}
                            transition = {{ duration: 1.25 }}
                        >
                            <ListItem disablePadding>
                                <StyledListItemButton selected={transaction_type == 'payment'} onClick={() => setTransactionType('payment')}>
                                    <ListItemIcon>
                                        <AddCard color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Cash-In" />
                                </StyledListItemButton>
                            </ListItem>
                        </motion.div>
                        <motion.div
                            initial    = {{ opacity: 0, x: -20 }}
                            animate    = {{ opacity: 1, x: 0 }}
                            transition = {{ duration: 1.5 }}
                        >
                            <ListItem disablePadding>
                                <StyledListItemButton selected={transaction_type == 'fund-transfer'} onClick={() => setTransactionType('fund-transfer')}>
                                    <ListItemIcon>
                                        <Payments color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Cash-Out" />
                                </StyledListItemButton>
                            </ListItem>
                        </motion.div>
                    </List>
                </StyledDrawer>
            </Grid2>
            <Grid2 size="grow" style={{padding: "24px", overflow: 'auto'}} columns={6}>
                {transaction_type == 'payment' &&
                    <Typography variant="h5" sx={{marginBottom: '1rem'}}>
                        Cash-In {' '} {summaryLoading && <CircularProgress size="24px" sx={{color: 'primary'}}/>}
                    </Typography>
                }
                {transaction_type == 'fund-transfer' &&
                    <Typography variant="h5" sx={{marginBottom: '1rem'}}>
                        Cash-Out {' '} {summaryLoading && <CircularProgress size="24px" sx={{color: 'primary'}}/>}
                    </Typography>
                }
                {
                    transaction_type == 'payment'? <Summary type={transaction_type} summary={paymentSummary}/>:
                    transaction_type == 'fund-transfer'? <Summary type={transaction_type} summary={disburseSummary}/>: <>
                        <Typography variant="h5">
                            Cash-In {' '} {summaryLoading && <CircularProgress size="24px" sx={{color: 'primary'}}/>}
                        </Typography>
                        <Summary type="payment" summary={paymentSummary}/>
                        <Typography variant="h5">
                            Cash-Out {' '} {summaryLoading && <CircularProgress size="24px" sx={{color: 'primary'}}/>}
                        </Typography>
                        <Summary type="fund-transfer" summary={disburseSummary}/>
                    </>
                }
                {transaction_type && <StyledCard sx={{margin: '0.5rem 0', minWidth: '400px'}}>
                    <CardContent>
                        <Grid2 container spacing={3} sx={{mt: '1rem', mx: '1rem'}}>
                            <Grid2 size={{xs: 12, sm: 6, md: 4}}>
                                <FormControl fullWidth>
                                    <FlatTextField
                                        size      = "small"
                                        variant   = "outlined"
                                        label     = "Search"
                                        value     = {search}
                                        slotProps = {{inputLabel: {shrink: true}, input: {startAdornment: <Search color="disabled"/>}}}
                                        onChange  = {e => setSearch(e.target.value)}
                                    />
                                </FormControl>
                            </Grid2>
                            <Grid2 size={{xs: 12, sm: 6, md: 2}}>
                                <FlatTextField fullWidth
                                    size      = "small"
                                    variant   = "outlined"
                                    label     = "Date"
                                    type      = "date"
                                    value     = {date}
                                    slotProps = {{inputLabel: {shrink: true}, input: {inputProps: {max: moment().format('YYYY-MM-DD')}}}}
                                    onChange  = {e => setDate(e.target.value)}
                                />
                            </Grid2>
                            <Grid2 size={{xs: 12, sm: 6, md: 2}}>
                                <FormControl fullWidth>
                                    <InputLabel id="cutoff-label">Cutoff</InputLabel>
                                    <FlatSelectField fullWidth
                                        id        = "cutoff"
                                        labelId   = "cutoff-label"
                                        label     = "Cutoff"
                                        size      = "small"
                                        value     = {cutoff}
                                        onChange = {e => setCutoff(e.target.value as any)}
                                    >
                                        <MenuItem value="All"><em>All</em></MenuItem>
                                        <MenuItem value="12AM">
                                            <Typography color="textSecondary">
                                                12AM - 8AM
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="8AM">
                                            <Typography color="textSecondary">
                                                8AM - 4PM
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="4PM">
                                            <Typography color="textSecondary">
                                                4PM - 12AM
                                            </Typography>
                                        </MenuItem>
                                    </FlatSelectField>
                                </FormControl>
                            </Grid2>
                            <Grid2 size={{xs: 12, sm: 6, md: 2}}>
                                <FormControl fullWidth>
                                    <InputLabel id="status-label">Status</InputLabel>
                                    <FlatSelectField fullWidth
                                        id        = "status"
                                        labelId   = "status-label"
                                        label     = "Status"
                                        size      = "small"
                                        value     = {status}
                                        onChange = {e => setStatus(e.target.value as string)}
                                    >
                                        <MenuItem value="All"><em>All</em></MenuItem>
                                        <MenuItem value="SUCCESS">
                                            <Typography color="success">
                                                Success
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="PENDING">
                                            <Typography color="warning">
                                                Pending
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="CLOSED">
                                            <Typography color="textSecondary">
                                                Closed
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="FAILED">
                                            <Typography color="error">
                                                Failed
                                            </Typography>
                                        </MenuItem>
                                    </FlatSelectField>
                                </FormControl>
                            </Grid2>
                            <Grid2 size={{xs: 12, md: 2}} sx={{ml: 'auto', mr: '0'}}>
                                <StyledButton
                                    sx       = {{marginTop: '0', marginBottom: '0'}}
                                    variant  = "contained"
                                    color    = "primary"
                                    onClick  = {e => loadData()}
                                    disabled = {loading}
                                >
                                    {loading? <CircularProgress size="24px"/>: "Apply"}
                                </StyledButton>
                            </Grid2>
                        </Grid2>
                    </CardContent>
                    <CardContent sx={{overflow: 'auto', maxHeight: '375px', paddingTop: 0}}>
                        <Table stickyHeader size="small">
                            <TableHead sx={{width: '100%'}}>
                                <TableRow>
                                    <StyledTableCell sortDirection={orderBy === "transaction_id" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "transaction_id"}
                                            direction = {orderBy === "transaction_id" ? order : 'asc'}
                                            onClick   = {() => orderData("transaction_id")}
                                        >
                                            Transaction ID
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "reference_id" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "reference_id"}
                                            direction = {orderBy === "reference_id" ? order : 'asc'}
                                            onClick   = {() => orderData("reference_id")}
                                        >
                                            Reference
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "instapay_reference" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "instapay_reference"}
                                            direction = {orderBy === "instapay_reference" ? order : 'asc'}
                                            onClick   = {() => orderData("instapay_reference")}
                                        >
                                            QRPH Reference No.
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "amount" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "amount"}
                                            direction = {orderBy === "amount" ? order : 'asc'}
                                            onClick   = {() => orderData("amount")}
                                        >
                                            Transaction Amount
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "amount" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "amount"}
                                            direction = {orderBy === "amount" ? order : 'asc'}
                                            onClick   = {() => orderData("amount")}
                                        >
                                            Amount
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "status" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "status"}
                                            direction = {orderBy === "status" ? order : 'asc'}
                                            onClick   = {() => orderData("status")}
                                        >
                                            Status
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "created_at" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "created_at"}
                                            direction = {orderBy === "created_at" ? order : 'asc'}
                                            onClick   = {() => orderData("created_at")}
                                        >
                                            Created
                                        </TableSortLabel>
                                    </StyledTableCell>
                                    <StyledTableCell sortDirection={orderBy === "paid_at" ? order : false}>
                                        <TableSortLabel
                                            active    = {orderBy === "paid_at"}
                                            direction = {orderBy === "paid_at" ? order : 'asc'}
                                            onClick   = {() => orderData("paid_at")}
                                        >
                                            Paid
                                        </TableSortLabel>
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{width: '100%'}}>
                            {visibleData.map((payment, index) => (
                                <TableRow key={index}>
                                    <StyledTableCell>{payment.transaction_id || '-'}</StyledTableCell>
                                    <StyledTableCell>{payment.reference_id || '-'}</StyledTableCell>
                                    <StyledTableCell>{payment.instapay_reference || '-'}</StyledTableCell>
                                    <StyledTableCell>₱{(+(payment.amount || 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}</StyledTableCell>
                                    <StyledTableCell>₱{((!transaction_type? 0: {
                                        payment        : +(payment.amount || 0) - +(payment.amount || 0)*CASHIN_FEE/100,
                                        'fund-transfer': +(payment.amount || 0) + CASHOUT_FEE
                                    }[transaction_type]) || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}</StyledTableCell>
                                    <StyledTableCell>
                                        <Typography color={statusColors[payment.status]} textTransform="capitalize">
                                            {payment.status?.toLowerCase() || '-'}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>{payment.created_at? moment(payment.created_at).utcOffset('+0800').format('YYYY-MM-DD HH:mm:ss'): '-'}</StyledTableCell>
                                    <StyledTableCell>{payment.paid_at? moment(payment.paid_at).utcOffset('+0800').format('YYYY-MM-DD HH:mm:ss'): '-'}</StyledTableCell>
                                    </TableRow>
                            ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                    <CardContent>
                        <StyledButton variant="contained" sx={{float: 'left', minWidth: '132.32px', minHeight: '33px'}} onClick={download} disabled={downloading}>
                            {downloading? <CircularProgress size="24px"/>: "Download (.xlsx)"}
                        </StyledButton>
                        <TablePagination
                            component    = "div"
                            count        = {data.length}
                            page         = {page}
                            onPageChange = {(e: any, page: number) => setPage(page == -1? 0: page)}
                            rowsPerPage  = {rowsPerPage}
                            rowsPerPageOptions={[
                                {value: 1000, label: '1,000'},
                                {value: 5000, label: '5,000'},
                                {value: 10000, label: '10,000'}
                            ]}
                            onRowsPerPageChange = {(e) => setRowsPerPage(+e.target.value)}
                        />
                    </CardContent>
                </StyledCard>}
            </Grid2>
        </DashboardBody>
    </>;
}

const Summary = ({type, summary}: any) => {

    return <>
            <Grid2 container spacing={2} sx={{mb: 3}}>
                {type == 'payment' && <Grid2 container size={{xs: 12, sm: 6, xl: 'grow'}}>
                    <StyledCard sx={{margin: '1rem 0'}}>
                        <Grid2 container>
                            {/* Total */}
                            <Grid2 size={{xs: 12, md: 6}}>
                                <CardContent>
                                    <Typography>
                                        Total Amount
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {(summary.TOTAL || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                            {/* Total (net) */}
                            <Grid2 size={{xs: 12, md: 6}}>
                                <CardContent>
                                    <Typography>
                                        Net Amount
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {((summary.TOTAL || 0)*0.987).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                        </Grid2>
                    </StyledCard>
                </Grid2>}
                {type == 'fund-transfer' && <Grid2 container size={{xs: 12, sm: 6, xl: 'grow'}}>
                    <StyledCard sx={{margin: '1rem 0'}}>
                        <Grid2 container>
                            {/* Total */}
                            <Grid2 size={{xs: 12, md: 6}}>
                                <CardContent>
                                    <Typography>
                                        Balance
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {(summary.BALANCE || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                            {/* Total (net) */}
                            <Grid2 size={{xs: 12, md: 6}}>
                                <CardContent>
                                    <Typography>
                                        Total
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {({
                                                "payment"      : +(summary.total || 0) - +(summary.total || 0)*CASHIN_FEE/100,
                                                "fund-transfer": +(summary.total || 0) + +(summary.count || 0)*CASHOUT_FEE
                                            }[type as string] || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                        </Grid2>
                    </StyledCard>
                </Grid2>}
                <Grid2 container size={{xs: 12, sm: 6, xl: 'grow'}}>
                    <StyledCard sx={{margin: '1rem 0'}}>
                        <Grid2 container>
                            {/* Transactions */}
                            <Grid2 size={{xs: 12, sm: 6, xl: 'grow'}}>
                                <CardContent>
                                    <Typography>
                                        Transactions
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {(summary.COUNT || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                            {/* Transactions Per Minute */}
                            <Grid2 size={{xs: 12, sm: 6, xl: 'grow'}}>
                                <CardContent>
                                    <Typography>
                                        Transactions / Min.
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {(summary.TRX_PER_MIN || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                        </Grid2>
                    </StyledCard>
                </Grid2>
                <Grid2 container size={{xs: 12}}>
                    <StyledCard>
                        {/* Success Rate */}
                        <Grid2 container>
                            {/* Success */}
                            <Grid2 size={{xs: 12, md: 3, lg: 'grow'}}>
                                <CardContent>
                                    <Typography>
                                        Success
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {(summary.SUCCESS || 0).toLocaleString(undefined, {maximumFractionDigits: 0})} {' '}
                                            <Typography display="inline" color="textDisabled">({((summary.SUCCESS || 0)/(summary.COUNT || 1)*100).toLocaleString(undefined, {maximumFractionDigits: 0})}%)</Typography>
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                            {/* Pending */}
                            <Grid2 size={{xs: 12, md: 3, lg: 'grow'}}>
                                <CardContent>
                                    <Typography>
                                        Pending
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {(summary.PENDING || 0).toLocaleString(undefined, {maximumFractionDigits: 0})} {' '}
                                            <Typography display="inline" color="textDisabled">({((summary.PENDING || 0)/(summary.COUNT || 1)*100).toLocaleString(undefined, {maximumFractionDigits: 0})}%)</Typography>
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                            {/* Closed */}
                            <Grid2 size={{xs: 12, md: 3, lg: 'grow'}}>
                                <CardContent>
                                    <Typography>
                                        Closed
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {(summary.CLOSED || 0).toLocaleString(undefined, {maximumFractionDigits: 0})} {' '}
                                            <Typography display="inline" color="textDisabled">({((summary.CLOSED || 0)/(summary.COUNT || 1)*100).toLocaleString(undefined, {maximumFractionDigits: 0})}%)</Typography>
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                            {/* Failed */}
                            <Grid2 size={{xs: 12, md: 3, lg: 'grow'}}>
                                <CardContent>
                                    <Typography>
                                        Failed
                                    </Typography>
                                    <motion.div
                                        initial    = {{ opacity: 0, y: -20 }}
                                        animate    = {{ opacity: 1, y: 0 }}
                                        transition = {{ duration: 1 }}
                                    >
                                        <Typography variant="h6">
                                            {(summary.FAILED || 0).toLocaleString(undefined, {maximumFractionDigits: 0})} {' '}
                                            <Typography display="inline" color="textDisabled">({((summary.FAILED || 0)/(summary.COUNT || 1)*100).toLocaleString(undefined, {maximumFractionDigits: 0})}%)</Typography>
                                        </Typography>
                                    </motion.div>
                                </CardContent>
                            </Grid2>
                        </Grid2>
                    </StyledCard>
                </Grid2>
            </Grid2>
    </>
}

export default Dashboard;